import { Link, useSearchParams } from "react-router";
import {
  findObjects,
  findSearchOptions,
  recogniseCisNumber,
} from "@shared-core/domain/object-ui/find";
import {
  useFindConsignment,
  useFindRecords,
  useFindTharsternRecords,
} from "@shared-core/domain/object-ui/hooks";
import { useObjectManifest } from "@shared-core/domain/object-ui/manifest";
import {
  isScopeOverrideRole,
  useScopeOverrideValue,
} from "@shared-core/domain/scopeOverride";
import { useAuth } from "../../app/auth/hooks";
import { Button } from "../../app/components/primitives/button";
import { Skeleton } from "../../app/components/primitives/skeleton";

/** Records shown per object; "Show all" opens the object's list searched for the text. */
const RESULTS_PER_OBJECT = 5;
/** A link that is not a record's name: as RefLinks draws one. */
const LINK = "text-primary underline-offset-2 hover:underline";

/**
 * Find's results (FB-055): what Enter in Find opens when no record is highlighted. Every searchable list is searched
 * for `?q=` through its own `q`, the objects people look up most first; a number's record, a consignment's orders
 * and a Tharstern number's records head the page as Find offers them. Nothing matched says so plainly, and each list
 * searched for the text is one click away either way.
 */
export function FindResultsPage() {
  const [searchParams] = useSearchParams();
  const text = (searchParams.get("q") ?? "").trim();
  const { user } = useAuth();
  const { showAll, setShowAll } = useScopeOverrideValue({ role: user?.role });
  // Keyed on the scope, so turning on Show all customers searches again with the header the account menu now sends.
  return (
    <FindResults
      key={showAll ? "all" : "mine"}
      text={text}
      isLimitedToPortfolio={isScopeOverrideRole(user?.role) && !showAll}
      onShowAllCustomers={() => setShowAll(true)}
    />
  );
}

export function FindResults({
  text,
  isLimitedToPortfolio,
  onShowAllCustomers,
}: {
  text: string;
  /** The person's customer lists hold only their portfolio, and they can turn on Show all customers. */
  isLimitedToPortfolio: boolean;
  onShowAllCustomers: () => void;
}) {
  const index = useObjectManifest();
  const { objects, searched } = findObjects(index);
  const match = text ? recogniseCisNumber(text, objects) : null;
  const consignments = useFindConsignment(text);
  const tharsternRecords = useFindTharsternRecords(text);
  const found = useFindRecords(text, searched, {
    perObject: RESULTS_PER_OBJECT,
    debounceMs: 0,
  });

  if (text === "") {
    return (
      <div className="space-y-2">
        <h1 className="text-2xl font-semibold">Find</h1>
        <p className="text-muted-foreground">
          Type a name, number or code in Find to search every list.
        </p>
      </div>
    );
  }

  const opens = [
    ...(match
      ? [
          {
            key: `cis-${match.number}`,
            label: `${match.object.label} ${match.number}`,
            detail: null as string | null,
            href: match.href,
          },
        ]
      : []),
    ...consignments.map((c) => ({
      key: `consignment-${c.orderNo}`,
      label: `Order ${c.orderNo}`,
      detail: c.hint as string | null,
      href: c.href,
    })),
    ...tharsternRecords.map((r) => ({
      key: `tharstern-${r.object.id}-${r.number}`,
      label: `${r.object.label} ${r.number}`,
      detail: r.title || null,
      href: r.href,
    })),
  ];
  const isNothingFound =
    !found.isSearching && found.groups.length === 0 && opens.length === 0;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">Results for “{text}”</h1>

      {isLimitedToPortfolio && (
        <div className="flex flex-wrap items-center gap-3 rounded-md border bg-muted/40 px-4 py-3 text-sm">
          <p>Find is showing only your customers’ records.</p>
          <Button size="sm" variant="outline" onClick={onShowAllCustomers}>
            Show all customers
          </Button>
        </div>
      )}

      {opens.length > 0 && (
        <section aria-labelledby="find-open" className="space-y-2">
          <h2 id="find-open" className="text-lg font-semibold">
            Open
          </h2>
          <ul className="divide-y rounded-md border">
            {opens.map((open) => (
              <li key={open.key} className="flex flex-wrap gap-x-3 px-4 py-2">
                <Link to={open.href} className="font-medium">
                  {open.label}
                </Link>
                {open.detail && (
                  <span className="text-muted-foreground">{open.detail}</span>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {found.groups.map((group) => {
        const headingId = `find-${group.object.id}`;
        return (
          <section
            key={group.object.id}
            aria-labelledby={headingId}
            className="space-y-2"
          >
            <div className="flex items-baseline gap-3">
              <h2 id={headingId} className="text-lg font-semibold">
                {group.object.plural}
              </h2>
              <span className="text-sm text-muted-foreground">
                {group.total === 1 ? "1 match" : `${group.total} matches`}
              </span>
            </div>
            <ul className="divide-y rounded-md border">
              {group.hits.map((hit) => (
                <li
                  key={`${hit.object}/${hit.key}`}
                  className="flex flex-wrap gap-x-3 px-4 py-2"
                >
                  <Link to={hit.href} className="font-medium">
                    {hit.title}
                  </Link>
                  {hit.detail && (
                    <span className="text-muted-foreground">{hit.detail}</span>
                  )}
                </li>
              ))}
            </ul>
            {group.total > group.hits.length && (
              <Link to={group.listHref} className={`text-sm ${LINK}`}>
                Show all {group.total} {group.object.plural.toLowerCase()}
              </Link>
            )}
          </section>
        );
      })}

      {found.isSearching && (
        <div
          role="status"
          aria-label="Searching every list"
          className="space-y-2"
        >
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      )}

      {isNothingFound && (
        <p role="status" className="text-muted-foreground">
          Nothing matched “{text}”.
        </p>
      )}

      {found.failed.length > 0 && (
        <p role="alert" className="text-sm text-destructive">
          Find couldn’t search{" "}
          {found.failed.map((o) => o.plural.toLowerCase()).join(", ")}.
        </p>
      )}

      <section aria-labelledby="find-lists" className="space-y-2">
        <h2 id="find-lists" className="text-lg font-semibold">
          Search one list for “{text}”
        </h2>
        <ul className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
          {findSearchOptions(text, searched).map((option) => (
            <li key={option.object.id}>
              <Link to={option.href} className={LINK}>
                {option.object.plural}
              </Link>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
